import React, { useContext, useState } from 'react'
import { observer } from 'mobx-react'
import './index.scss'
import InputText from '@Components/Fields/InputText'
import Dropdown from '@Components/Selectors/Dropdown'
import Button from '@Components/Buttons/Button'
import { IAkaTitleValidation } from '@App/interfaces/validations'
import { getValueForm } from '@App/helpers/get-value-form'
import { formFieldsInvalidCheck } from '@App/helpers/work-entities/entities-validators'
import { createUpdatingWorkDraft } from '@App/helpers/work-entities/creators'
import { StoresContext } from '@App/contexts'
import { OptionModel } from '@Entities/lib'
import { Link } from 'react-router'
import { AlternativeTitleModel } from '@Entities/work/alternative-title-model'

interface AddAlternativeTitleProps {
    closePopupFunc(): void
}

const AddAlternativeTitle = ({ closePopupFunc }: AddAlternativeTitleProps) => {
    const { workStore, libStore } = useContext(StoresContext)

    const [alternativeTitleData, setAlternativeTitleData] = useState<AlternativeTitleModel>({
        title: '',
        titleType: null,
        languageCode: null,
    })

    const [validations, setValidations] = useState<IAkaTitleValidation>({
        title: {
            required: true,
            maxLength: 500,
        },
        titleType: {
            required: true,
            isNumber: true,
        },
        languageCode: {
            isLanguage: true,
        },
    })

    const [isDisabled, setIsDisabled] = useState<boolean>(true)

    const getValues = (value: string | number | boolean | null, id: string) => {
        const { formObject, validationObject } = getValueForm(value, id, alternativeTitleData, validations)

        setAlternativeTitleData({ ...formObject })
        setValidations({ ...validationObject })

        checkForm()
    }

    const checkForm = () => {
        setIsDisabled(formFieldsInvalidCheck(alternativeTitleData, validations))
    }

    const addAkaTitleToDraft = () => {
        const workDraftFromLocal = workStore.workDraftLocal

        if (!workDraftFromLocal) {
            return
        }

        const updatedWork = createUpdatingWorkDraft<AlternativeTitleModel>(
            workDraftFromLocal,
            'alternativeTitles',
            alternativeTitleData
        )

        workStore.setWorkDraftToLocal(updatedWork)
        closePopupFunc()
    }

    const workDraftToLocal = workStore.workDraftLocal
    const languageOptions: OptionModel[] = libStore.languages
    const titleTypeOptions: OptionModel[] = libStore.akaTitleTypes

    if (!alternativeTitleData || !validations || !workDraftToLocal || !languageOptions || !titleTypeOptions) {
        return null
    }

    const { title, titleType, languageCode } = alternativeTitleData

    return (
        <div className="alternative-titles-sub-form-component">
            <h2>Alternative Titles</h2>
            <p className="title">
                <p className="tooltip">
                    For full definitions of each Title Type, please click{' '}
                    <Link
                        className="tooltip"
                        to="https://help.themlc.com/en/support/what-is-a-title-type"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        here.
                    </Link>
                </p>
            </p>
            <div className="inputs-field">
                <InputText
                    mainColor
                    id="title"
                    label="Alternative Title"
                    assistiveText="Required"
                    value={title}
                    onChange={getValues}
                    errorMessage={validations.title.error}
                    required={validations.title.required}
                />
                <Dropdown
                    options={titleTypeOptions.filter((option) => +option.id !== 1)}
                    id="titleType"
                    label="Title Type"
                    assistiveText="Required"
                    mainColor
                    isSearchable
                    isNumberType
                    onChange={getValues}
                    errorMessage={validations.titleType.error}
                    required={validations.titleType.required}
                    selectedValue={titleType ? titleTypeOptions.find((option) => +option.id === titleType) : null}
                />
                <Dropdown
                    options={languageOptions}
                    id="languageCode"
                    label="Language"
                    mainColor
                    isSearchable
                    isNumberType
                    onChange={getValues}
                    errorMessage={validations.languageCode.error}
                    required={validations.languageCode.required}
                    selectedValue={languageCode ? languageOptions.find((option) => +option.id === languageCode) : null}
                />
            </div>
            <div className="add-title-container">
                <Button mainColor type="primary" disabled={isDisabled} onClick={addAkaTitleToDraft}>
                    Add Title
                </Button>
            </div>
        </div>
    )
}

export default observer(AddAlternativeTitle)
